// components/ConsultationScheduling.jsx
import React, { useState } from 'react';
import '../assets/css/ConsultationScheduling.css';
import { supabase } from '../lib/supabase';

const ConsultationScheduling = ({ onClose = () => {} }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    company: '',
    message: '',
    preferredDate: '',
    preferredTime: '',
    timezone: 'EST'
  });
  
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [captchaValue, setCaptchaValue] = useState('');
  const [captchaText, setCaptchaText] = useState('');
  const [isVisible, setIsVisible] = useState(true);

  // Country codes with flags and formatting patterns
  const countryCodes = [
    { code: '+1', flag: '🇺🇸', name: 'US/Canada', pattern: /^(\d{0,3})(\d{0,3})(\d{0,4})$/ },
    { code: '+44', flag: '🇬🇧', name: 'UK', pattern: /^(\d{0,4})(\d{0,3})(\d{0,4})$/ },
    { code: '+61', flag: '🇦🇺', name: 'Australia', pattern: /^(\d{0,1})(\d{0,4})(\d{0,4})$/ },
    { code: '+49', flag: '🇩🇪', name: 'Germany', pattern: /^(\d{0,5})(\d{0,7})$/ },
    { code: '+33', flag: '🇫🇷', name: 'France', pattern: /^(\d{0,3})(\d{0,3})(\d{0,3})$/ },
    { code: '+81', flag: '🇯🇵', name: 'Japan', pattern: /^(\d{0,4})(\d{0,4})$/ },
    { code: '+86', flag: '🇨🇳', name: 'China', pattern: /^(\d{0,3})(\d{0,4})(\d{0,4})$/ },
    { code: '+91', flag: '🇮🇳', name: 'India', pattern: /^(\d{0,5})(\d{0,5})$/ },
    { code: '+34', flag: '🇪🇸', name: 'Spain', pattern: /^(\d{0,3})(\d{0,3})(\d{0,3})$/ },
    { code: '+39', flag: '🇮🇹', name: 'Italy', pattern: /^(\d{0,3})(\d{0,3})(\d{0,4})$/ },
    { code: '+7', flag: '🇷🇺', name: 'Russia', pattern: /^(\d{0,3})(\d{0,3})(\d{0,4})$/ },
    { code: '+52', flag: '🇲🇽', name: 'Mexico', pattern: /^(\d{0,3})(\d{0,3})(\d{0,4})$/ },
    { code: '+55', flag: '🇧🇷', name: 'Brazil', pattern: /^(\d{0,2})(\d{0,5})(\d{0,4})$/ },
    { code: '+27', flag: '🇿🇦', name: 'South Africa', pattern: /^(\d{0,2})(\d{0,3})(\d{0,4})$/ },
    { code: '+63', flag: '🇵🇭', name: 'Philippines', pattern: /^(\d{0,4})(\d{0,4})(\d{0,4})$/ },
    { code: '+82', flag: '🇰🇷', name: 'South Korea', pattern: /^(\d{0,3})(\d{0,4})(\d{0,4})$/ },
  ];

  // Validation functions
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Full name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        if (!/^[a-zA-Z\s]+$/.test(value.trim())) return 'Name can only contain letters and spaces';
        return '';
      
      case 'email':
        if (!value) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
        return '';
      
      case 'phone':
        if (value) {
          const digitsOnly = value.replace(/\D/g, '');
          // const country = countryCodes.find(cc => cc.code === formData.countryCode);
          
          if (digitsOnly.length < 7) return 'Phone number is too short';
          if (digitsOnly.length > 15) return 'Phone number is too long';
          if (!/^\d+$/.test(digitsOnly)) return 'Phone number can only contain digits';
        }
        return '';
      
      case 'message':
        if (!value.trim()) return 'Message is required';
        if (value.trim().length < 10) return 'Message must be at least 10 characters';
        return '';
      
      default:
        return '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    newErrors.name = validateField('name', formData.name);
    newErrors.email = validateField('email', formData.email);
    newErrors.message = validateField('message', formData.message);
    newErrors.phone = validateField('phone', formData.phone);
    
    if (!captchaValue) {
      newErrors.captcha = 'Security check is required';
    } else if (captchaValue.toUpperCase() !== captchaText) {
      newErrors.captcha = 'Security code does not match';
    }
    
    setErrors(newErrors);
    return Object.values(newErrors).every(error => error === '');
  };

  const generateCaptcha = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let text = '';
    for (let i = 0; i < 5; i++) {
      text += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaText(text);
    return text;
  };

  React.useEffect(() => {
    generateCaptcha();
  }, []);

  const formatPhoneNumber = (value, countryCode) => {
    const digitsOnly = value.replace(/\D/g, '');
    const country = countryCodes.find(cc => cc.code === countryCode);
    
    if (!country || !digitsOnly) return value;
    
    const match = digitsOnly.match(country.pattern);
    if (!match) return digitsOnly;
    
    const parts = match.slice(1).filter(part => part !== '');
    return parts.join(' ');
  };

  const handleCountryCodeChange = (e) => {
    const newCountryCode = e.target.value;
    const formattedPhone = formatPhoneNumber(formData.phone, newCountryCode);
    
    setFormData(prev => ({
      ...prev,
      countryCode: newCountryCode,
      phone: formattedPhone
    }));

    if (touched.phone) {
      setErrors(prev => ({
        ...prev,
        phone: validateField('phone', formattedPhone)
      }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const digitsOnly = value.replace(/\D/g, '');
    const formattedValue = formatPhoneNumber(value, formData.countryCode);
    
    setFormData(prev => ({
      ...prev,
      phone: formattedValue
    }));

    if (touched.phone) {
      setErrors(prev => ({
        ...prev,
        phone: validateField('phone', digitsOnly)
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      handlePhoneChange(e);
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (touched[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: validateField(name, value)
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '');
      setErrors(prev => ({
        ...prev,
        [name]: validateField(name, digitsOnly)
      }));
    } else {
      setErrors(prev => ({
        ...prev,
        [name]: validateField(name, value)
      }));
    }
  };

  const handleCaptchaChange = (e) => {
    const value = e.target.value;
    setCaptchaValue(value);
    
    if (touched.captcha) {
      setErrors(prev => ({
        ...prev,
        captcha: value.toUpperCase() !== captchaText ? 'Security code does not match' : ''
      }));
    }
  };

  const refreshCaptcha = () => {
    generateCaptcha();
    setCaptchaValue('');
    setErrors(prev => ({ ...prev, captcha: '' }));
  };

  // FIXED: Proper close function
  const handleClose = () => {
    console.log('Closing modal...');
    setIsVisible(false);
    // Call the parent's onClose function if it exists
    if (typeof onClose === 'function') {
      onClose();
    } else {
      console.warn('onClose is not a function, using local state only');
    }
  };

  // FIXED: Overlay click handler
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const allTouched = Object.keys(formData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, { captcha: true });
    setTouched(allTouched);
    
    if (!validateForm()) {
      setSubmitStatus('validation_error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const fullPhoneNumber = formData.phone ? 
        `${formData.countryCode} ${formData.phone}` : '';

      const { error } = await supabase
        .from('consultations')
        .insert([{
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: fullPhoneNumber,
          company: formData.company.trim(),
          message: formData.message.trim(),
          type: 'consultation',
          status: 'new',
          preferred_date: formData.preferredDate,
          preferred_time: formData.preferredTime,
          timezone: formData.timezone
        }])
        .select();

      if (error) throw error;
      
      setSubmitStatus('success');
      setFormData({ 
        name: '', 
        email: '', 
        phone: '',
        countryCode: '+1',
        company: '', 
        message: '',
        preferredDate: '',
        preferredTime: '',
        timezone: 'EST'
      });
      setCaptchaValue('');
      setErrors({});
      setTouched({});
      generateCaptcha();
      
      setTimeout(() => {
        handleClose();
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting consultation:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const timeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'
  ];

  const timezones = [
    { value: 'EST', label: 'Eastern Time (EST)' },
    { value: 'PST', label: 'Pacific Time (PST)' },
    { value: 'CST', label: 'Central Time (CST)' },
    { value: 'MST', label: 'Mountain Time (MST)' }
  ];

  const getCurrentCountry = () => {
    return countryCodes.find(cc => cc.code === formData.countryCode);
  };

  // Don't render if not visible
  if (!isVisible) {
    return (
      <div className="thank-you-message" style={{textAlign: 'center', padding: '2rem'}}>
        <h2>Thank you for using this website!</h2>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content consultation-modal">
        <div className="modal-header">
          <h3>Schedule a Consultation</h3>
          <button 
            className="modal-close" 
            onClick={handleClose}
            aria-label="Close modal"
            type="button"
          >
            ×
          </button>
        </div>
        
        <div className="modal-body">
          <div className="consultation-scheduling-modal">
            <div className="consultation-form-container">
              <form className="consultation-form" onSubmit={handleSubmit} noValidate>
                <h2>Contact Information</h2>
                
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required
                      disabled={isSubmitting}
                      placeholder="Your full name"
                      className={errors.name ? 'error' : ''}
                    />
                    {errors.name && <span className="error-text">{errors.name}</span>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required
                      disabled={isSubmitting}
                      placeholder="your.email@example.com"
                      className={errors.email ? 'error' : ''}
                    />
                    {errors.email && <span className="error-text">{errors.email}</span>}
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">Phone Number</label>
                      <div className="phone-input-container">
                        <div className="form-group country-code-select">
                          <select
                            value={formData.countryCode}
                            onChange={handleCountryCodeChange}
                            disabled={isSubmitting}
                          >
                            {countryCodes.map(country => (
                              <option key={country.code} value={country.code}>
                                {country.flag} {country.code}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group phone-number-input">
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handlePhoneChange}
                            onBlur={handleBlur}
                            disabled={isSubmitting}
                            placeholder={`e.g., ${getCurrentCountry()?.name === 'US/Canada' ? '555 123 4567' : 'phone number'}`}
                            className={errors.phone ? 'error' : ''}
                          />
                          {errors.phone && <span className="error-text">{errors.phone}</span>}
                        </div>
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="company">Company</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={isSubmitting}
                        placeholder="Your company name"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="preferredDate">Preferred Date</label>
                      <input
                        type="date"
                        id="preferredDate"
                        name="preferredDate"
                        value={formData.preferredDate}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="preferredTime">Preferred Time</label>
                      <select
                        id="preferredTime"
                        name="preferredTime"
                        value={formData.preferredTime}
                        onChange={handleChange}
                        disabled={isSubmitting}
                      >
                        <option value="">Select a time</option>
                        {timeSlots.map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="timezone">Timezone</label>
                    <select
                      id="timezone"
                      name="timezone"
                      value={formData.timezone}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    >
                      {timezones.map(tz => (
                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">How can we help you? *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      rows="3"
                      required
                      disabled={isSubmitting}
                      placeholder="Briefly describe what you're looking for..."
                      className={errors.message ? 'error' : ''}
                    />
                    {errors.message && <span className="error-text">{errors.message}</span>}
                  </div>

                  <div className="captcha-group">
                    <label>Security Check *</label>
                    <div className="captcha-container">
                      <div className="captcha-display">
                        <span className="captcha-text">{captchaText}</span>
                        <button 
                          type="button" 
                          onClick={refreshCaptcha}
                          className="captcha-refresh"
                          disabled={isSubmitting}
                        >
                          ↻
                        </button>
                      </div>
                      <input
                        type="text"
                        className={`captcha-input ${errors.captcha ? 'error' : ''}`}
                        value={captchaValue}
                        onChange={handleCaptchaChange}
                        onBlur={() => setTouched(prev => ({ ...prev, captcha: true }))}
                        placeholder="Enter code above"
                        disabled={isSubmitting}
                        required
                      />
                    </div>
                    {errors.captcha && <span className="error-text">{errors.captcha}</span>}
                  </div>

                  {submitStatus === 'success' && (
                    <div className="status-message success">
                      ✅ Thank you! We'll contact you within 24 hours to schedule your consultation.
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="status-message error">
                      ❌ Error submitting form. Please try again or contact us directly.
                    </div>
                  )}
                  
                  {submitStatus === 'validation_error' && (
                    <div className="status-message error">
                      ❌ Please fix the errors above before submitting.
                    </div>
                  )}

                  <div className="form-actions">
                    <button 
                      type="button" 
                      className="btn-secondary" 
                      onClick={handleClose}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Schedule Consultation'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationScheduling;